import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Container, Row, Column, TabsList, List, Carousel } from '@pella/sash-framework';
import { Layout, SEO, Breadcrumbs, CTA, Image, Video } from '../../components';

import ImageBanner from '../../components/common/image-banner';
import LinkList from '../../components/common/link-list';
import ProductSeries from '../../components/common/product-series';
import Feature from '../../components/common/feature';
import PerformancePanel from '../../components/common/performance-panel';
import DynamicComponent from '../../components/common/dynamic-component';
import ImageGrid from '../../components/common/image-grid';
import CardSection from '../../components/common/card-section';
import HeroSimple from '../../components/common/hero-simple';

import styles from './index.module.scss';

const ReservePLLP = (props) => {
	const pageData = props.data.contentstackReservePllp;

	return (
		<Layout id='template-ReservePLLP'>
			{pageData.breadcrumb && (
				<Breadcrumbs
					data={pageData.breadcrumb}
					currentUrl={pageData.url}
				/>
			)}

			{pageData.page_hero?.[0] && <HeroSimple data={pageData.page_hero[0]} />}

			{pageData.anchor_links?.[0] && (
				<div className='bg-black py-5 md:py-10'>
					<Container>
						<LinkList
							className={styles.reserveLinks}
							links={pageData.anchor_links}
							variant='white'
						/>
					</Container>
				</div>
			)}

			{pageData.modular_blocks?.[0] &&
				pageData.modular_blocks.map((block, index) => {
					if (block.style_banner != null) {
						return (
							<div
								id={block.style_banner.anchor_text || null}
								key={index}
							>
								{block.style_banner.banner_style === 'style' && <ProductSeries data={block.style_banner} />}
								{block.style_banner.banner_style === 'feature' && <Feature data={block.style_banner} />}
							</div>
						);
					}

					if (block.all_dynamic?.dynamic?.[0]) {
						return (
							<DynamicComponent
								data={block.all_dynamic}
								key={index}
							/>
						);
					}

					if (block.performance?.performance?.[0]) {
						return (
							<PerformancePanel
								data={block.performance.performance[0]}
								key={index}
							/>
						);
					}

					if (block.callouts?.callout?.[0]) {
						return (
							<div
								key={index}
								id={block.callouts.anchor_text || ''}
								className={`${styles.sectionWrapper} ${styles.tabs} ${styles[`tabs_${index}`]}`}
							>
								<Container>
									{block.callouts.heading && (
										<h2
											className={styles.sectionTitle}
											dangerouslySetInnerHTML={{
												__html: block.callouts.heading,
											}}
										/>
									)}
									{block.callouts.description && (
										<div
											className={styles.tabsIntro}
											dangerouslySetInnerHTML={{
												__html: block.callouts.description,
											}}
										/>
									)}

									{block.callouts.callout?.[0] && (
										<TabsList theme='dark'>
											{block.callouts.callout.map((tab, index) => (
												<TabsList.Tab
													key={index}
													title={tab.tab}
												>
													<Row>
														<Column
															xs={12}
															md={4}
														>
															{block.callouts.image?.[0] && (
																<Image
																	data={block.callouts.image[0]}
																	className={styles.tabs_MainImage}
																/>
															)}

															{tab.position && (
																<span
																	className={`${styles.tabs_FocusDot} ${styles[`tabs_FocusDot_${index}`]}`}
																	style={{
																		left: `${tab.position.left * 1}%`,
																		top: `${tab.position.top * 1}%`,
																	}}
																/>
															)}
														</Column>

														<Column
															xs={12}
															md={8}
															className={styles.tabs_ContentColumn}
														>
															{tab.detail?.image?.[0] && (
																<Image
																	data={tab.detail.image[0]}
																	className={styles.tabs_TabImage}
																/>
															)}
															<div className={styles.tabs_Content}>
																{tab.detail.heading && <h3>{tab.detail.heading}</h3>}

																{tab.detail.description && (
																	<div
																		dangerouslySetInnerHTML={{
																			__html: tab.detail.description,
																		}}
																	/>
																)}
															</div>
														</Column>
													</Row>
												</TabsList.Tab>
											))}
										</TabsList>
									)}
								</Container>
							</div>
						);
					}

					if (block.pros?.pro?.[0]) {
						return (
							<div
								id={block.pros.anchor_text || ''}
								className={`${styles.sectionWrapper} ${styles.pros}`}
								key={index}
							>
								<Container>
									{block.pros.heading && <h2 className={`${styles.sectionTitle} s2-h2__alt`}>{block.pros.heading}</h2>}

									<Carousel
										className='proSlider'
										showArrows
									>
										{block.pros.pro?.[0] &&
											block.pros.pro.map((pro, index) => (
												<React.Fragment key={index}>
													{pro.grid?.[0] && <ImageGrid data={pro.grid[0]} />}

													{pro.quote && (
														<blockquote
															className={styles.prosSlider_BlockQuote}
															dangerouslySetInnerHTML={{
																__html: pro.quote,
															}}
														/>
													)}
												</React.Fragment>
											))}
									</Carousel>
								</Container>
							</div>
						);
					}

					if (block.banner?.banner?.[0]) {
						return (
							<ImageBanner
								data={block.banner.banner[0]}
								key={index}
							/>
						);
					}

					if (block.left_right?.columns?.[0]) {
						return (
							<div
								id={block.left_right.anchor_text || null}
								key={index}
								className={styles.reserveColumns}
							>
								<Container>
									<Row
										align={block.left_right.align}
										justify={block.left_right.columns[0].justify}
										verticalGutters
										largeGutters
									>
										{block.left_right.columns.map((column, index) => (
											<Column
												xs={column.column_mobile}
												lg={column.column_desktop}
												key={index}
											>
												{column.image?.[0]?.image?.url && (
													<Image
														data={column.image[0]}
														className={styles.reserveColumns_Image}
													/>
												)}
												{(column.image?.[0]?.video?.video_id || column.image?.[0]?.internal_video?.url) && (
													<Video
														data={column.image[0]}
														className={styles.reserveColumns_Video}
													/>
												)}
												{column.subheading && (
													<div
														className={styles.reserveColumns_Subheading}
														dangerouslySetInnerHTML={{
															__html: column.subheading,
														}}
													/>
												)}
												{column.heading && (
													<h2
														className={styles.reserveColumns_Heading}
														dangerouslySetInnerHTML={{
															__html: column.heading,
														}}
													/>
												)}
												{column.description && (
													<div
														dangerouslySetInnerHTML={{
															__html: column.description,
														}}
													/>
												)}
												{column.ctas?.[0] && (
													<List
														bullets='none'
														className={styles.reserveColumns_Ctas}
													>
														{column.ctas.map((cta, index) => (
															<li key={index}>
																<CTA data={cta} />
															</li>
														))}
													</List>
												)}
											</Column>
										))}
									</Row>
								</Container>
							</div>
						);
					}

					if (block.cards != null) {
						return (
							<CardSection
								data={block.cards}
								key={index}
							/>
						);
					}
				})}
		</Layout>
	);
};

ReservePLLP.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
};

export default ReservePLLP;

// eslint-disable-next-line react/prop-types -- fix this
export const Head = ({ data }) => <SEO {...data.contentstackReservePllp.seo} />;

export const query = graphql`
	query ($uid: String!) {
		contentstackReservePllp(id: { eq: $uid }) {
			url
			breadcrumb {
				active_page
				back_pages {
					...CTA
				}
			}
			ratings_code {
				ratings_code
			}
			page_hero {
				...NewHero
			}
			anchor_links {
				...CTA
			}
			modular_blocks {
				style_banner {
					anchor_text
					banner_style
					image {
						...Image
						image {
							url
							gatsbyImageData(quality: 90)
						}
					}
					subheading
					heading
					content_position
					content {
						heading
						description
						ctas {
							...CTA
						}
					}
				}
				all_dynamic {
					anchor_text
					heading
					dynamic {
						...Dynamic
					}
				}
				performance {
					anchor_text
					performance {
						...Performance
					}
				}
				callouts {
					anchor_text
					image {
						...Image
						image {
							url
							gatsbyImageData(quality: 90, width: 540)
						}
					}
					heading
					description
					callout {
						position {
							left
							top
						}
						tab
						detail {
							image {
								...Image
								image {
									url
									gatsbyImageData(quality: 90, width: 1110)
								}
							}
							heading
							description
						}
					}
				}
				pros {
					anchor_text
					heading
					pro {
						grid {
							...ImageGrid
						}
						quote
					}
				}
				banner {
					banner {
						...ImageCallout
					}
				}
				left_right {
					anchor_text
					align
					columns {
						column_desktop
						column_mobile
						justify
						image {
							... on Contentstack_image {
								...Image
								image {
									url
									gatsbyImageData(quality: 90)
								}
							}
							... on Contentstack_video {
								...Video
							}
						}
						subheading
						heading
						description
						ctas {
							...CTA
						}
					}
				}
				cards {
					...CardSection
				}
			}

			seo {
				title
				description
				noIndex: no_index_page
			}
		}
	}
`;
